import { ChevronDown, CircleHelp, Hand, MessageCircle, RotateCcw, SendHorizontal, Sparkles, X } from "lucide-react";
import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import type { Persona, WidgetTheme } from "@navibiz/chat-core";
import styles from "./WidgetPreview.module.css";

const LAUNCHER_ICON: Record<WidgetTheme["launcherIcon"], ReactNode> = {
  chat: <MessageCircle aria-hidden />,
  sparkle: <Sparkles aria-hidden />,
  help: <CircleHelp aria-hidden />,
  wave: <Hand aria-hidden />,
};

/**
 * A faithful, static rendering of the widget for the settings form — the
 * specialist or client sees every colour, label, shape, and animation change
 * before saving, without loading the real widget against a live key. The
 * launcher is clickable, so opening and closing play the chosen animation.
 */
export function WidgetPreview({
  persona,
  theme,
  locale,
  projectName,
  onLocaleChange,
}: {
  persona: Persona;
  theme: WidgetTheme;
  locale: "id" | "en";
  projectName: string;
  onLocaleChange: (locale: "id" | "en") => void;
}) {
  const [open, setOpen] = useState(true);
  const [closing, setClosing] = useState(false);
  // Bumped to remount the panel, which replays its opening animation.
  const [play, setPlay] = useState(0);
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);
  useEffect(() => () => clearTimeout(timer.current), []);

  const style = {
    "--accent": theme.accent,
    "--on-accent": theme.accentContrast,
    "--radius": `${theme.radius}px`,
    fontFamily: theme.fontFamily || undefined,
  } as CSSProperties;
  const initial = persona.name.trim()[0]?.toUpperCase() ?? "A";
  const photo = theme.avatarMode === "photo" && theme.avatarUrl ? theme.avatarUrl : null;
  const avatar = theme.avatarMode === "none" ? null : <span className={styles.avatar}>{photo ? <img src={photo} alt="" /> : initial}</span>;
  const left = theme.position === "left" ? styles.left : "";

  const toggle = () => {
    clearTimeout(timer.current);
    if (open && !closing) {
      setClosing(true);
      timer.current = setTimeout(() => (setOpen(false), setClosing(false)), 220);
    } else {
      setClosing(false);
      setOpen(true);
      setPlay((n) => n + 1);
    }
  };

  const face =
    theme.launcherStyle === "pill" ? (
      <>
        {avatar}
        {theme.launcherLabel[locale] || "…"}
      </>
    ) : photo ? (
      <img className={styles.facePhoto} src={photo} alt="" />
    ) : theme.avatarMode === "initial" ? (
      <span className={styles.faceInitial}>{initial}</span>
    ) : (
      LAUNCHER_ICON[theme.launcherIcon]
    );

  return (
    <div className={styles.wrap}>
      <div className={`${styles.stage} ${theme.surface === "dark" ? styles.dark : ""}`} style={style} aria-label="Pratinjau widget">
        {open && (
          <div key={play} className={`${styles.panel} ${left} ${styles[`anim_${theme.animation}`]} ${closing ? styles.closing : ""}`}>
            <div className={`${styles.header} ${avatar ? "" : styles.noAvatar}`}>
              {avatar}
              <div className={styles.headerText}>
                <div className={styles.name}>{persona.name || "Asisten"}</div>
                <div className={styles.status}>
                  <i /> {locale === "id" ? "Online · dijawab asisten AI" : "Online · answered by an AI assistant"}
                </div>
              </div>
              {theme.languageSwitch && (
                <div className={styles.lang} role="group" aria-label="Bahasa widget">
                  {(["id", "en"] as const).map((value) => (
                    <button key={value} type="button" aria-pressed={locale === value} onClick={() => onLocaleChange(value)}>
                      {value.toUpperCase()}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className={styles.body}>
              <div className={styles.bubble}>{persona.greeting[locale] || "…"}</div>
              <div className={styles.chips}>
                {persona.suggestions[locale].slice(0, 4).map((suggestion) => (
                  <span key={suggestion}>{suggestion}</span>
                ))}
              </div>
            </div>
            <div className={styles.composer}>
              <span>{locale === "id" ? "Tulis pertanyaan…" : "Type your question…"}</span>
              <b>
                <SendHorizontal aria-hidden />
              </b>
            </div>
            <div className={styles.footer}>{projectName} · Powered by Navibiz</div>
          </div>
        )}

        {!open && theme.launcherStyle === "teaser" && (
          <div className={`${styles.teaser} ${left}`}>
            <span>{theme.launcherLabel[locale] || "…"}</span>
            <X aria-hidden />
          </div>
        )}

        <button
          type="button"
          className={`${styles.launcher} ${styles[`launcher_${theme.launcherStyle}`]} ${left} ${!open ? styles[`attention_${theme.attention}`] : ""} ${theme.launcherStyle === "pill" && !avatar ? styles.noAvatar : ""}`}
          onClick={toggle}
          aria-label={open ? "Tutup pratinjau widget" : "Buka pratinjau widget"}
        >
          {open && !closing ? <ChevronDown aria-hidden className={styles.closeIcon} /> : face}
        </button>
      </div>
      <p className={styles.hint}>
        <RotateCcw aria-hidden /> Klik tombol widget untuk melihat animasi buka-tutup dan tampilan saat tertutup.
      </p>
    </div>
  );
}
